import { Component, computed, input, OnInit, OnDestroy, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { ButtonComponent } from '../button/button.component';
import { NotificationConfig } from 'shared';

@Component({
  selector: 'ds-notification',
  imports: [CommonModule, IconComponent, ButtonComponent],
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
  host: {
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
  },
})
export class NotificationComponent implements OnInit, OnDestroy {
  public config = input.required<NotificationConfig>();
  public dismiss = output<NotificationConfig>();

  protected progress = signal(0);
  protected isPaused = signal(false);
  protected animationDuration = computed(() => `${this.config().duration || 0}ms`);

  private timeoutId?: number;
  private startTime?: number;
  private remainingTime?: number;

  public ngOnInit(): void {
    const duration = this.config().duration;
    if (duration) {
      this.startTimer(duration);
    }
  }

  public ngOnDestroy(): void {
    this.clearTimer();
  }

  private startTimer(duration: number): void {
    this.startTime = Date.now();
    this.remainingTime = duration;

    this.timeoutId = window.setTimeout(() => {
      this.closeNotification();
    }, duration);
  }

  private clearTimer(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
  }

  protected onMouseEnter(): void {
    if (this.config().duration) {
      this.isPaused.set(true);
      this.clearTimer();

      // Calculate remaining time
      if (this.startTime && this.remainingTime) {
        const elapsed = Date.now() - this.startTime;
        this.remainingTime = Math.max(0, this.remainingTime - elapsed);
      }
    }
  }

  protected onMouseLeave(): void {
    if (this.config().duration && this.remainingTime) {
      this.isPaused.set(false);
      this.startTimer(this.remainingTime);
    }
  }

  protected icon = computed(() => {
    switch (this.config().variant) {
      case 'success':
        return 'circle-check';
      case 'info':
        return 'info-circle';
      case 'error':
        return 'alert-circle';
      case 'warning':
        return 'alert-triangle';

      default:
        return 'info-circle';
    }
  });

  protected cssClasses = computed(() => [`notification--${this.config().variant}`]);

  protected closeNotification() {
    this.dismiss.emit(this.config());
  }
}
